<template>
  <div class="quarantine-selector">
    <div class="calculator-intro">
      <h3>{{ t('quarantine.subtitle').value }}</h3>
      <p>{{ t('quarantine.description').value }}</p>
    </div>

    <div class="plan-selection">
      <div class="form-section">
        <h4>{{ t('quarantine.sections.basicSettings').value }}</h4>
        <div class="form-group">
          <label for="water-volume">{{ t('quarantine.labels.waterVolume').value }}</label>
          <input
            type="number"
            id="water-volume"
            v-model="waterVolume"
            class="form-control"
            :placeholder="t('quarantine.placeholders.waterVolume').value"
            step="0.1"
            min="1"
          >
          <small class="form-hint">{{ t('quarantine.labels.waterVolumeHint').value }}</small>
        </div>

        <div class="form-group">
          <label for="fish-type">{{ t('quarantine.labels.fishType').value }}</label>
          <select
            id="fish-type"
            v-model="selectedFishType"
            class="select-control"
          >
            <option value="all">{{ t('quarantine.options.fishType.all').value }}</option>
            <option value="wild-clownfish">{{ t('quarantine.options.fishType.wildClownfish').value }}</option>
            <option value="wrasses">{{ t('quarantine.options.fishType.wrasses').value }}</option>
            <option value="tangs">{{ t('quarantine.options.fishType.tangs').value }}</option>
            <option value="angelfish">{{ t('quarantine.options.fishType.angelfish').value }}</option>
            <option value="other">{{ t('quarantine.options.fishType.other').value }}</option>
          </select>
        </div>
      </div>

      <div class="plan-options">
        <h4>{{ t('quarantine.sections.planSelection').value }}</h4>
        <div class="plan-cards">
          <div
            v-for="plan in filteredPlans"
            :key="plan.id"
            class="plan-card"
            :class="{ 'selected': selectedPlan?.id === plan.id }"
            @click="selectPlan(plan)"
          >
            <div class="plan-header">
              <h5>{{ t(plan.name).value }}</h5>
              <span class="duration-badge">{{ plan.duration }} {{ t('quarantine.units.days').value }}</span>
            </div>
            <p class="plan-description">{{ t(plan.description).value }}</p>
            
            <div class="plan-details">
              <div class="detail-item">
                <span class="detail-label">{{ t('quarantine.labels.applicableFish').value }}:</span>
                <span class="detail-value">{{ plan.fishTypes.map(ft => t(ft).value).join(', ') }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">{{ t('quarantine.labels.stepCount').value }}:</span>
                <span class="detail-value">{{ plan.steps.length }}</span>
              </div>
            </div>

            <div v-if="plan.warnings.length > 0" class="plan-warnings">
              <div class="warning-icon">⚠️</div>
              <div class="warning-text">
                {{ t(plan.warnings[0]).value }}
                <span v-if="plan.warnings.length > 1">{{ t('quarantine.warnings.moreItems', { count: plan.warnings.length }).value }}</span>
              </div>
            </div>

            <div class="plan-actions">
              <button
                class="select-btn"
                :class="{ 'selected': selectedPlan?.id === plan.id }"
              >
                {{ selectedPlan?.id === plan.id ? t('quarantine.buttons.selected').value : t('quarantine.buttons.selectPlan').value }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="selectedPlan" class="selected-plan-details">
        <div class="results-card">
          <h4>{{ t('quarantine.selectedPlan.title', { name: t(selectedPlan.name).value }).value }}</h4>
          <div class="plan-summary">
            <div class="result-item">
              <span class="result-label">{{ t('quarantine.labels.totalDuration').value }}:</span>
              <span class="result-value">{{ selectedPlan.duration }} {{ t('quarantine.units.days').value }}</span>
            </div>
            <div class="result-item">
              <span class="result-label">{{ t('quarantine.labels.steps').value }}:</span>
              <span class="result-value">{{ selectedPlan.steps.length }} {{ t('quarantine.units.steps').value }}</span>
            </div>
            <div class="result-item">
              <span class="result-label">{{ t('quarantine.labels.waterVolumeValue').value }}:</span>
              <span class="result-value">{{ waterVolume }} {{ t('quarantine.units.liter').value }}</span>
            </div>
          </div>

          <div v-if="selectedPlan.warnings.length > 0" class="warnings-section">
            <h5>⚠️ {{ t('quarantine.labels.importantNotes').value }}</h5>
            <ul class="warning-list">
              <li v-for="(warning, index) in selectedPlan.warnings" :key="index">
                {{ t(warning).value }}
              </li>
            </ul>
          </div>

          <div class="action-buttons">
            <button
              class="calculate-btn"
              @click="startQuarantine"
              :disabled="!canStart"
            >
              {{ t('quarantine.buttons.startQuarantine').value }}
            </button>
            <button
              class="reset-btn"
              @click="resetSelection"
            >
              {{ t('quarantine.buttons.resetSelection').value }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useI18n } from '../composables/useI18n'
import { QUARANTINE_PLANS, type QuarantinePlan } from '../composables/useQuarantinePlan'

// Props
interface Props {
  modelValue?: QuarantinePlan | null
  initialWaterVolume?: number
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  initialWaterVolume: 20
})

// Emits
const emit = defineEmits<{
  'update:modelValue': [value: QuarantinePlan | null]
  'start': [plan: QuarantinePlan, waterVolume: number]
}>()

const { t } = useI18n()

// 响应式数据
const selectedPlan = ref<QuarantinePlan | null>(props.modelValue)
const waterVolume = ref<number>(props.initialWaterVolume)
const selectedFishType = ref<string>('all')

// 计算属性
const filteredPlans = computed(() => {
  if (selectedFishType.value === 'all') {
    return QUARANTINE_PLANS
  }

  return QUARANTINE_PLANS.filter(plan => {
    if (selectedFishType.value === 'wild-clownfish') {
      return plan.fishTypes.includes('野生小丑鱼')
    } else if (selectedFishType.value === 'wrasses') {
      return plan.fishTypes.includes('隆头鱼')
    }
    return true
  })
})

const canStart = computed(() => {
  return selectedPlan.value && waterVolume.value > 0
})

// 方法
const selectPlan = (plan: QuarantinePlan) => {
  selectedPlan.value = plan
  emit('update:modelValue', plan)
}

const startQuarantine = () => {
  if (selectedPlan.value && waterVolume.value > 0) {
    emit('start', selectedPlan.value, waterVolume.value)
  }
}

const resetSelection = () => {
  selectedPlan.value = null
  waterVolume.value = props.initialWaterVolume
  selectedFishType.value = 'all'
  emit('update:modelValue', null)
}

// 监听props变化
watch(() => props.modelValue, (newValue) => {
  selectedPlan.value = newValue
})

watch(() => props.initialWaterVolume, (newValue) => {
  waterVolume.value = newValue
})
</script>

<style scoped>
.quarantine-selector {
  max-width: 100%;
  margin: 0 auto;
  padding: 0;
}

.plan-selection {
  display: grid;
  gap: 2rem;
}

.form-section {
  background: #ffffff;
  padding: 1.5rem;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.form-section h4 {
  margin-bottom: 1rem;
  color: #333333;
}

.plan-options h4 {
  margin-bottom: 1rem;
  color: #333333;
}

.plan-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
}

.plan-card {
  background: #ffffff;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.plan-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 15px rgba(0, 0, 0, 0.1);
  border-color: #667eea;
}

.plan-card.selected {
  border-color: #667eea;
  background: linear-gradient(135deg, #ffffff, rgba(102, 126, 234, 0.1));
  box-shadow: 0 10px 15px rgba(0, 0, 0, 0.1);
}

.plan-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
}

.plan-header h5 {
  color: #333333;
  margin: 0;
  flex: 1;
  margin-right: 1rem;
}

.duration-badge {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;
}

.plan-description {
  color: #666666;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  line-height: 1.5;
}

.plan-details {
  margin-bottom: 1rem;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}

.detail-label {
  color: #666666;
}

.detail-value {
  color: #333333;
  font-weight: 500;
}

.plan-warnings {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.75rem;
  background: rgba(255, 193, 7, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 6px;
  margin-bottom: 1rem;
}

.warning-icon {
  font-size: 1.125rem;
  line-height: 1;
}

.warning-text {
  color: #ffc107;
  font-size: 0.875rem;
  line-height: 1.4;
  flex: 1;
}

.plan-actions {
  text-align: center;
}

.select-btn {
  width: 100%;
  padding: 0.5rem 1rem;
  border: 1px solid #667eea;
  border-radius: 6px;
  background: transparent;
  color: #667eea;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
}

.select-btn:hover {
  background: #667eea;
  color: #ffffff;
}

.select-btn.selected {
  background: #667eea;
  color: #ffffff;
}

.selected-plan-details {
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  overflow: hidden;
}

.results-card {
  padding: 1.5rem;
}

.results-card h4 {
  color: #667eea;
  margin-bottom: 1rem;
}

.plan-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: rgba(102, 126, 234, 0.05);
  border-radius: 6px;
}

.result-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.result-label {
  color: #666666;
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
}

.result-value {
  color: #333333;
  font-weight: 500;
  font-size: 1.125rem;
}

.warnings-section {
  margin-bottom: 1.5rem;
}

.warnings-section h5 {
  color: #ffc107;
  margin-bottom: 0.75rem;
}

.warning-list {
  color: #666666;
  padding-left: 1.5rem;
}

.warning-list li {
  margin-bottom: 0.5rem;
  line-height: 1.4;
}

.action-buttons {
  display: flex;
  gap: 1rem;
}

.calculate-btn {
  flex: 2;
  padding: 1rem 1.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.calculate-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 10px 15px rgba(0, 0, 0, 0.1);
}

.calculate-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.reset-btn {
  flex: 1;
  padding: 1rem 1.5rem;
  background: transparent;
  color: #666666;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.reset-btn:hover {
  background: #e9ecef;
  color: #333333;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .plan-cards {
    grid-template-columns: 1fr;
  }
  
  .plan-summary {
    grid-template-columns: 1fr;
  }
  
  .action-buttons {
    flex-direction: column;
  }
  
  .form-section {
    padding: 1rem;
  }
}
</style>